import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { RootState } from 'src/redux/store';
import { sseService } from 'src/common/services/sseService';

/**
 * Custom hook dedicated to Orbit AI Server-Sent Events (SSE).
 * Registers Orbit AI's `agent-update` listener on the shared `sseService` singleton.
 *
 * Guarantees a single active SSE connection across all mounted Orbit AI components/pages
 * and prevents duplicate connections when dashboard or other app pages are active.
 */
export const useOrbitAISSE = (customSiteId?: string | number) => {
  const queryClient = useQueryClient();
  const reduxSiteId =
    useSelector((state: RootState) => state.siteAudit.siteId) ?? 0;
  const siteId = customSiteId || reduxSiteId;
  const siteIdRef = useRef(siteId);

  useEffect(() => {
    siteIdRef.current = siteId;
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;

    sseService.subscribe('orbit-ai', {
      siteId,
      eventHandlers: {
        'agent-update': (updates: any) => {
          const agentUpdates = Array.isArray(updates) ? updates : [updates];

          agentUpdates.forEach((agentData: any) => {
            if (!agentData) return;
            const activeSiteId = siteIdRef.current;

            // for blog generation
            if (agentData.agentId === 'blog-generation') {
              queryClient.invalidateQueries({
                queryKey: ['agentsStatus', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['getUserAgentDetails', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['blog-blueprints']
              });
              queryClient.removeQueries({
                queryKey: ['get-generated-blog']
              });
              queryClient.removeQueries({
                queryKey: ['get-generated-blog-by-blueprint']
              });
            }

            // for local-seo agent
            if (agentData.agentId === 'local-seo') {
              queryClient.invalidateQueries({
                queryKey: ['agentsStatus', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['getUserAgentDetails', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['get-local-seo-report']
              });
            }

            // for keyword-agent
            if (agentData.agentId === 'keyword-intelligence') {
              queryClient.invalidateQueries({
                queryKey: ['agentsStatus', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['getUserAgentDetails', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['keywords-assistance']
              });
            }

            // for content strategy agent
            if (agentData.agentId === 'content-strategy') {
              queryClient.invalidateQueries({
                queryKey: ['agentsStatus', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['getUserAgentDetails', activeSiteId]
              });
              queryClient.invalidateQueries({
                queryKey: ['content-strategy']
              });
            }
          });
        }
      }
    });

    return () => {
      sseService.unsubscribe('orbit-ai');
    };
  }, [siteId, queryClient]);
};

export default useOrbitAISSE;
